import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsShowingUserSheet,
  selectUsersName,
  selectCompanyName,
  selectLogonUrls,
  setGoToResetPassword,
  setGoToUserDetails,
  setGoToConfigureNotifications,
} from 'store/appSlice';
import { UserAvatarForDrawer } from '../UserAvatar';
import {
  clearSession,
  redirectToLogin,
  redirectToPartnerLogin,
} from 'lib/router';
import { hasValue } from 'swipeclock-frontend';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useUser } from 'src/store/userSlice';

export default function PartnerMain() {
  return (
    <>
      <Header />
      <Body>
        <User />
      </Body>
      <Footer />
    </>
  );
}

//##################################################################
// ### CHILDREN COMPONENTS
// User is a list of actions
// Header is the user's name and company name
// Footer is a divider and empty space
//##################################################################

function User() {
  const user = useUser();
  const dispatch = useDispatch();
  const logonUrls = useSelector(selectLogonUrls);

  const isSuperAdmin = user?.hasSuperAdminRole;

  const handleResetPassword = () => {
    dispatch(setGoToResetPassword({ goToResetPassword: true }));
  };

  const handleSignOut = () => {
    clearSession(dispatch);
    if (logonUrls?.usePartnerLogonUrl) {
      return redirectToPartnerLogin(logonUrls);
    }
    return redirectToLogin(logonUrls);
  };

  const handleUserDetails = () => {
    dispatch(setGoToUserDetails({ goToUserDetails: true }));
  };

  const handleConfigureNotifications = () => {
    dispatch(
      setGoToConfigureNotifications({ goToConfigureNotifications: true })
    );
  };

  return (
    <List dense>
      <ListItemButton onClick={handleUserDetails}>
        <ListItemText primary="Update Personal Information" />
      </ListItemButton>
      {!isSuperAdmin && (
        <ListItemButton onClick={handleConfigureNotifications}>
          <ListItemText primary="Configure Notifications" />
        </ListItemButton>
      )}
      <ListItemButton onClick={handleResetPassword}>
        <ListItemText primary="Reset Password" />
      </ListItemButton>
      <ListItemButton onClick={handleSignOut}>
        <ListItemText primary="Sign Out" />
      </ListItemButton>
    </List>
  );
}

function Header() {
  const dispatch = useDispatch();
  const usersName = useSelector(selectUsersName);
  const companyName = useSelector(selectCompanyName);
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing(1),
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <UserAvatarForDrawer />
          <Stack direction="column" spacing={0}>
            {hasValue(usersName) && (
              <Typography
                variant="body1"
                sx={{ color: theme.palette.custom.navyBlue.main }}
              >
                {usersName}
              </Typography>
            )}
            {hasValue(companyName) && (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: theme.typography.fontWeightLight }}
              >
                {companyName}
              </Typography>
            )}
          </Stack>
        </Stack>
        <IconButton
          onClick={() => dispatch(setIsShowingUserSheet({ showing: false }))}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
    </>
  );
}

function Footer() {
  return (
    <>
      <Divider />
      <Box sx={{ height: '56px', width: '100%' }} />
    </>
  );
}

//##################################################################
// ### STYLED COMPONENTS
//##################################################################

const Body = styled(Box)(({ _theme }) => ({
  display: 'flex',
  flexGrow: 1,
  '& ul': {
    width: '100%',
  },
}));
